import { useEffect, useMemo, useRef } from 'react';
import useReply from './useReply';
import useEdit from './useEdit';
import debounce from '../utils/debounce';
import draftStorageService from '../services/draftStorageService';

/**
 * Manages message drafts, including reply and edit states, for a given room.
 * It automatically saves drafts to storage and restores them when switching rooms.
 * @param {string} roomId - The ID of the current room.
 * @param {string} value - The current text value of the message input.
 * @param {function} onSetMessage - Function to set the message input's text.
 * @param {function} onSetSending - Function to set the isSending state.
 * @returns {object} An object containing reply and edit state and their respective handlers.
 */
export const useMessageDrafts = (roomId, value, onSetMessage, onSetSending) => {
  const { replyingTo, startReply, cancelReply, getReplyContent, restoreReplyFromStorage } = useReply(roomId);
  const { editingMessage, startEdit, cancelEdit, getEditContent, restoreEditFromStorage } = useEdit(roomId);
  const isRestoringRef = useRef(false);

  const saveDraftDebounced = useMemo(
    () => debounce((currentRoomId, text, replyTo, editing) => {
      if (isRestoringRef.current) return;

      if (!text && !replyTo && !editing) {
        draftStorageService.clearDraft(currentRoomId);
        return;
      }

      draftStorageService.saveDraft(currentRoomId, {
        text: text || '',
        replyTo: replyTo || null,
        editing: editing || null,
      });
    }, 500),
    [],
  );

  // Restore draft when room changes
  useEffect(() => {
    if (!roomId) return;

    saveDraftDebounced.cancel();
    isRestoringRef.current = true;

    const draft = draftStorageService.getDraft(roomId);

    if (draft) {
      if (draft.text) onSetMessage(draft.text);
      if (draft.replyTo) restoreReplyFromStorage(draft.replyTo);
      if (draft.editing) restoreEditFromStorage(draft.editing);
    } else {
      onSetMessage('');
      cancelReply();
      cancelEdit();
    }

    if (onSetSending) {
      onSetSending(false);
    }

    setTimeout(() => {
      isRestoringRef.current = false;
    }, 50);

    return () => saveDraftDebounced.cancel();
  }, [roomId, onSetMessage, restoreReplyFromStorage, restoreEditFromStorage, cancelReply, cancelEdit, saveDraftDebounced, onSetSending]);

  // Save draft when text, reply, or edit state changes
  useEffect(() => {
    if (roomId && !isRestoringRef.current) {
      saveDraftDebounced(roomId, value, replyingTo, editingMessage);
    }
  }, [value, replyingTo, editingMessage, roomId, saveDraftDebounced]);

  return {
    replyingTo,
    startReply,
    cancelReply,
    getReplyContent,
    editingMessage,
    startEdit,
    cancelEdit,
    getEditContent,
    restoreEditFromStorage,
    isRestoringRef, // Expose for use in other effects
  };
};